// https://www.benmvp.com/blog/sync-localstorage-react-usereducer-hook/
import type { UseLocalStorageKeysKeys } from "@dealer-center/core-interfaces-components";
import {
  getLocalStorageKeyValue,
  getMaxLength
} from "@dealer-center/core-interfaces-components";
import { useCallback, useReducer } from "react";
import { useLocalStorageHook } from "./useLocalStorageHook";

/**
 * Actions available in the useLocalStorageReducer
 */
export const useLocalStorageReducerActions = {
  add: "addItemToLocalStorageList",
  remove: "removeItemFromLocalStorageList",
  toggle: "toggleItemInLocalStorageList",
  set: "setLocalStorageList"
} as const;

export type UseLocalStorageReducerActionsKeys =
  (typeof useLocalStorageReducerActions)[keyof typeof useLocalStorageReducerActions];

export type UseLocalStorageReducerDispatch<TItem> = ReturnType<
  typeof useLocalStorageReducer<TItem>
>[1];

/**
 * Defines the action objects that can be dispatched to the reducer
 */
export type UseLocalStorageReducerAction<TItem> =
  | { action: "addItemToLocalStorageList"; payload: TItem }
  | { action: "setLocalStorageList"; payload: TItem[] }
  | { action: "removeItemFromLocalStorageList"; payload: TItem }
  | { action: "toggleItemInLocalStorageList"; payload: TItem };

/**
 * Adds an item to the beginning of a list while removing duplicates
 * and ensuring the list doesn't exceed the maximum length
 *
 * @param existingList - The current list of items
 * @param itemToAdd - The item to add to the list
 * @param maxLength - Maximum number of items allowed in the list
 * @returns A new list with the item added and duplicates removed
 */
function addItemToList<TListItem>(
  existingList: TListItem[],
  itemToAdd: TListItem,
  maxLength: number
): TListItem[] {
  const itemToAddStr = JSON.stringify(itemToAdd);
  const existingListWithoutItem = existingList.filter(
    item => JSON.stringify(item) !== itemToAddStr
  );

  if (existingListWithoutItem.length >= maxLength) {
    existingListWithoutItem.pop();
  }

  const result = [itemToAdd, ...existingListWithoutItem];
  return result;
}

/**
 * Retrieves an array from localStorage for a given key
 *
 * @param key - The storage key to retrieve from
 * @returns The parsed array or an empty array if not found or invalid
 */
export const getArrayFromLocalStorage = <
  TLocalStorageItem extends string | number
>(
  key: UseLocalStorageKeysKeys
): TLocalStorageItem[] => {
  const storageKey = getLocalStorageKeyValue(key);
  const item = window.localStorage.getItem(storageKey);
  try {
    const values = item ? JSON.parse(item) : [];

    if (
      Array.isArray(values) &&
      values.every(
        (value: TLocalStorageItem) =>
          typeof value === "string" || typeof value === "number"
      )
    ) {
      return values as TLocalStorageItem[];
    }
    return [];
  } catch (e) {
    return [];
  }
};

/**
 * Ensures an array doesn't exceed the maximum length defined for a storage key
 *
 * @param array - The array to check and potentially truncate
 * @param key - The storage key that defines the maximum length
 * @returns A new array that doesn't exceed the maximum length
 */
export const reduceArrayToMatchMaxLength = <TItemType>(
  array: TItemType[],
  key: UseLocalStorageKeysKeys
): TItemType[] => {
  const maxLength = getMaxLength(key);
  return array.slice(0, maxLength);
};

export type UseLocalStorageReducerType<TItem> = [
  TItem[],
  React.Dispatch<UseLocalStorageReducerAction<TItem>>
];

/**
 * A hook that combines useReducer with localStorage to manage array-based state
 * that persists across sessions. Handles common operations like adding, removing,
 * toggling items and setting the entire list.
 *
 * @param localStorageKey - The key to store the array under (use key.keyName)
 * @param initialState - Optional initial state if nothing exists in storage
 * @returns A tuple with the current state array and a dispatch function
 *
 * @example
 * // Basic usage for favorites
 * const [favorites, dispatch] = useLocalStorageReducer(key.ddcAccountFavorites, []);
 *
 * // Adding an item
 * dispatch({
 *   action: useLocalStorageReducerActions.add,
 *   payload: "item1"
 * });
 *
 * // Removing an item
 * dispatch({
 *   action: useLocalStorageReducerActions.remove,
 *   payload: "item1"
 * });
 */
export const useLocalStorageReducer = <TStateItem>(
  localStorageKey: UseLocalStorageKeysKeys,
  initialState?: TStateItem[]
): UseLocalStorageReducerType<TStateItem> => {
  const [savedState, saveState] = useLocalStorageHook(
    localStorageKey,
    initialState
  );
  const maxLength = getMaxLength(localStorageKey);

  const reducerLocalStorage = useCallback(
    (state: TStateItem[], action: UseLocalStorageReducerAction<TStateItem>) => {
      const reducer = (
        state: TStateItem[],
        action: UseLocalStorageReducerAction<TStateItem>
      ) => {
        switch (action.action) {
          case useLocalStorageReducerActions.add: {
            // if item already exists add it to the start
            const newState = addItemToList(state, action.payload, maxLength);
            saveState(newState);
            return newState;
          }
          case useLocalStorageReducerActions.remove: {
            const newState = state.filter(item => item !== action.payload);
            saveState(newState);
            return newState;
          }
          case useLocalStorageReducerActions.toggle: {
            const newState = state.includes(action.payload)
              ? state.filter(item => item !== action.payload)
              : addItemToList(state, action.payload, maxLength);
            saveState(newState);
            return newState;
          }
          case useLocalStorageReducerActions.set: {
            saveState(action.payload);
            return action.payload;
          }
          default:
            return state;
        }
      };
      const newState = reducer(state, action);

      saveState(newState);

      return newState;
    },
    [maxLength, saveState]
  );

  const initialStateValue = (savedState || initialState || []).slice(
    0,
    maxLength
  );

  return useReducer(reducerLocalStorage, initialStateValue);
};
